import { useEffect, useRef } from 'react';
import { useLocation } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import useItemStore from '@/stores/useItem';
import { getFolderByIdQueryKey } from '@/hooks/queries/folder';

/**
 * ScrollTrackAndRestore Component
 * 
 * A component that automatically tracks and restores scroll positions across navigation.
 * It integrates with TanStack Query to handle scroll restoration after data loading.
 * 
 * Features:
 * - Tracks scroll position by pathname in sessionStorage
 * - Restores scroll after TanStack Query data loads
 * - Handles both cached data (immediate restore) and fresh fetches (waits for completion)
 * - Detects folder query state changes and restores accordingly
 * - Uses mutation observer to handle dynamic content wrapper mounting
 * 
 * Integration:
 * - Place this component in your root layout or main router component
 * - Requires a scrollable element with id="content-wrapper"
 * 
 * @returns null - This is a headless utility component
 */
function ScrollTrackAndRestore() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const pathname = location.pathname;
  // const folderId = pathname.split('/').pop();
  const folderId = useItemStore(state=> state.parentId);
  const groupId = useItemStore(state => state.groupId);
  const observerRef = useRef<MutationObserver | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const currentPathnameRef = useRef(pathname);

  /**
   * Set up scroll listener that persists across navigation
   * Uses a ref to track current pathname without recreating the listener
   */
  useEffect(() => {
    /**
     * Attaches scroll event listener to content wrapper
     * @returns {boolean} True if listener was attached successfully
     */
    function attachScrollListener() {
      const contentWrapper = document.getElementById('content-wrapper');
      if (!contentWrapper) return false;

      /**
       * Handles scroll events and saves position to sessionStorage
       * @param {Event} event - The scroll event
       */
      const handleScroll = (event: Event) => {
        // Save scroll position for current pathname
        sessionStorage.setItem(
          currentPathnameRef.current,
          (event.target as HTMLElement).scrollTop.toString()
        );
      };

      contentWrapper.addEventListener('scroll', handleScroll);
      cleanupRef.current = () => {
        contentWrapper.removeEventListener('scroll', handleScroll);
      };
      return true;
    }

    // Try to attach listener immediately
    if (!attachScrollListener()) {
      // If content wrapper not found, observe DOM for changes
      observerRef.current = new MutationObserver(() => {
        if (attachScrollListener()) {
          observerRef.current?.disconnect();
        }
      });
      observerRef.current.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      cleanupRef.current?.();
      observerRef.current?.disconnect();
    };
  }, []); // No dependency on pathname - listener stays alive across navigation

  /**
   * Save scroll position before navigation and update pathname reference
   * This ensures we don't lose scroll position when navigating away
   */
  useEffect(() => {
    // Save scroll position for the previous pathname before changing
    if (currentPathnameRef.current !== pathname) {
      const contentWrapper = document.getElementById('content-wrapper');
      if (contentWrapper) {
        sessionStorage.setItem(currentPathnameRef.current, contentWrapper.scrollTop.toString());
      }
    }

    currentPathnameRef.current = pathname;
  }, [pathname]);

  /**
   * Main scroll restoration logic
   * Handles different scenarios: cached data, fresh fetches, and loading states
   */
  useEffect(() => {
    const savedScroll = sessionStorage.getItem(pathname);

    if (!savedScroll) return;

    /**
     * Restores scroll position to the saved value
     */
    const restoreScroll = () => {
      const contentWrapper = document.getElementById('content-wrapper');
      if (contentWrapper) {
        contentWrapper.scrollTo({ top: parseInt(savedScroll) });
      }
    };

    // Check current query state — must match the exact key useGetFolderById queries under
    // (['folder', id, groupId]), or getQueryState/getQueryCache lookups below never find it.
    const queryKey = getFolderByIdQueryKey({ id: folderId, groupId });
    const queryData = queryClient.getQueryState(queryKey);

    // Determine if this is a fresh fetch or cached data
    const hasValidData = queryData?.status === 'success' && queryData?.data;

    // Handle immediate restoration for cached data
    if (hasValidData && queryData?.fetchStatus !== 'fetching') {
      setTimeout(restoreScroll, 150);
      return;
    }

    // For fresh fetches, wait for completion
    let hasRestored = false;

    /**
     * Subscribe to query cache changes to detect when data loading completes
     */
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      const eventQueryKey = event.query.queryKey;
      const isOurQuery = JSON.stringify(eventQueryKey) === JSON.stringify(queryKey);

      if (hasRestored) return;

      // Restore scroll when our folder query completes successfully
      if (
        isOurQuery &&
        event.query.state.status === 'success' &&
        event.query.state.data &&
        event.query.state.fetchStatus === 'idle'
      ) {
        hasRestored = true;
        setTimeout(restoreScroll, 150);
        unsubscribe();
      }
    });

    /**
     * Polling mechanism as fallback to detect query completion
     * Useful when subscription events might be missed
     */
    let pollCount = 0;
    const pollInterval = setInterval(() => {
      pollCount++;
      const currentQueryData = queryClient.getQueryState(queryKey);

      // Check if query completed successfully
      if (
        !hasRestored &&
        currentQueryData?.status === 'success' &&
        currentQueryData?.data &&
        currentQueryData?.fetchStatus === 'idle'
      ) {
        hasRestored = true;
        restoreScroll();
        unsubscribe();
        clearInterval(pollInterval);
      }

      // Stop polling after 10 attempts (20 seconds)
      if (pollCount >= 10) {
        clearInterval(pollInterval);
      }
    }, 2000);

    /**
     * Final fallback timeout to ensure scroll restoration
     * Triggers after 2 seconds if other methods haven't worked
     */
    const fallbackTimeout = setTimeout(() => {
      if (!hasRestored) {
        const currentQueryData = queryClient.getQueryState(queryKey);
        if (currentQueryData?.status === 'success' && currentQueryData?.data) {
          hasRestored = true;
          restoreScroll();
          unsubscribe();
        }
      }
    }, 2000);

    return () => {
      unsubscribe();
      clearInterval(pollInterval);
      clearTimeout(fallbackTimeout);
    };
  }, [pathname, queryClient, folderId, groupId]);

  return null;
}

export default ScrollTrackAndRestore;
